import { checkIsPublicRoute } from '@nxtpeople/nxt-vue-keycloak';
import axios, { AxiosError } from 'axios';
import { NavigationGuard, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import i18n from '../i18n';
import { publicRoutes } from '../router';
import store from '../store';

const AUTH_GUARD: NavigationGuard = async (to, _from, next) => {
   if (checkIsPublicRoute(to, publicRoutes)) return next();

   /* Checking if the user is trying to access the error page and if the error page has the correct query params. */
   checkErrorPage(to, next);

   /* Fetching the user information from the backend. */
   await fetchUserInfo(next);

   /* Redirect to user's default view on initial load */
   if (shouldRedirectToDefaultView(to, _from)) {
      const defaultView = store.state.userInfo?.userInfo?.default_view?.replaceAll('_', '-');
      if (defaultView) {
         next(`/${defaultView}`);
         return;
      }
   }

   next();
};

function checkErrorPage(to: RouteLocationNormalized, next: NavigationGuardNext) {
   const isErrorPage = to.name === 'Error';
   const hasErrorParams = to.query.error_code && to.query.error_title;

   if (isErrorPage) {
      if (!hasErrorParams) return next('/');
      //user will not be fetched so loading can be disabled
      store.state.userInfo!.loadingUserInfo = false;
      return next();
   }
}

function checkUserInfoHasCountry(next: NavigationGuardNext) {
   if (!store.state.userInfo?.userInfo?.default_team?.city?.country) {
      next({
         name: 'Error',
         query: {
            error_code: '400',
            error_title: i18n.global.t('error.user_no_country_set'),
            error_message: i18n.global.t('error.user_no_country_set_message'),
            error_action: 'logout',
         },
      });
   }
}

function shouldRedirectToDefaultView(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
   // Only redirect on initial load when user is at the root path
   const isInitialLoad = !from.name || from.path === '/';
   const isRootPath = to.path === '/';

   return isInitialLoad && isRootPath;
}

async function fetchUserInfo(next: NavigationGuardNext) {
   if (!store.state.userInfo?.loadingUserInfo) {
      checkUserInfoHasCountry(next);
      return;
   }
   if (!store.state.userInfo?.userInfo) {
      await store.dispatch('userInfo/getUserInfo').catch((err: AxiosError) => {
         const isCancelException = axios.isCancel(err);
         if (isCancelException) return;
         next({
            name: 'Error',
            query: {
               error_code: '500',
               error_title: i18n.global.t('generic.something-went-wrong'),
               error_message: i18n.global.t('errors.account-not-found'),
               error_action: 'logout',
            },
         });
      });

      checkUserInfoHasCountry(next);
   }
}

export default AUTH_GUARD;
